import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const DEFAULTS = {
  parallel: 't-fade',
  back: 'scroll-x-transition',
  forward: 'scroll-x-reverse-transition',
}

/**
 * Composable for handling route transitions
 * Replaces the Vue 2 RouteTransition mixin
 *
 * @returns {Object} Route transition state and methods
 */
export function useRouteTransition() {
  // State
  const transitionName = ref(null)
  const transitioning = ref(false)

  // Get current route
  const route = useRoute()

  // Watch for route changes
  watch(route, (to, from) => {
    transitionName.value = getRouteTransition(to, from)
  })

  /**
   * Get the appropriate transition based on routes
   *
   * @param {Object} to - Target route
   * @param {Object} from - Source route
   * @returns {String} Transition name to use
   */
  const getRouteTransition = (to, from) => {
    let transition = null

    // Check for transition in route meta
    for (const r of to.matched) {
      if (r.meta.transition) transition = r.meta.transition
    }

    // Execute transition function if provided
    if (typeof transition === 'function') {
      transition = transition({
        transitionName: transitionName.value,
        transitioning: transitioning.value
      })
    }

    // Return directly if transition is a string
    if (typeof transition === 'string') {
      return transition
    }

    // Determine transition based on path depth
    const toDepth = to.path.split('/').length
    const fromDepth = from.path.split('/').length

    // Merge with defaults
    transition = {
      ...DEFAULTS,
      ...transition,
    }

    // Choose transition based on depth comparison
    if (toDepth === fromDepth) {
      return transition.parallel
    }

    return toDepth < fromDepth ? transition.back : transition.forward
  }

  return {
    transitionName,
    transitioning,
    getRouteTransition
  }
}
